import React, { useState } from 'react';
import { Box, Table, Thead, Tbody, Tr, Th, Td, Button, IconButton, Flex, Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton, useDisclosure, FormControl, FormLabel, Input, Textarea, Grid, Select, Badge, VStack, HStack, Image, Text, SimpleGrid, useToast, Tabs, TabList, Tab, TabPanels, TabPanel, TableContainer, Icon } from '@chakra-ui/react';
import { EditIcon, DeleteIcon, AddIcon, CloseIcon, ChevronLeftIcon, ChevronRightIcon, ChevronDownIcon, ViewIcon } from '@chakra-ui/icons';
import { FaClipboardList, FaClock, FaSpinner, FaCheckCircle } from 'react-icons/fa';
import { getComplaints, createComplaint, updateComplaint, deleteComplaint, getTechnicians, getServices, BASE_URL, getComplaintInventory, getServiceReportPdf, getUploadUrl, lookupCustomerByPhone } from '../Utilities/networks';
import Services from './Services';
import ComplaintsMap from './ComplaintsMap';
import PageShell from './ui/PageShell';
import DataCard from './ui/DataCard';
import StatTile from './ui/StatTile';
import LocationPicker from './ui/LocationPicker';
import AddressAutocomplete from './ui/AddressAutocomplete';

const formatWhatsAppPhone = (phone) => {
  let digits = String(phone).replace(/[\s\-\(\)\+]/g, '');
  if (digits.startsWith('0')) {
    digits = digits.slice(1);
  }
  // Indian mobile numbers are often stored as 10 digits without country code.
  if (digits.length === 10 && /^[6-9]/.test(digits)) {
    digits = `91${digits}`;
  }
  return digits;
};

const normalizePhoneDigits = (phone) => {
  const digits = String(phone || '').replace(/\D/g, '');
  return digits.length >= 10 ? digits.slice(-10) : digits;
};

const Complaints = () => {
  // Updated complaints list with new fields - now fetched from API
  const [complaintsList, setComplaintsList] = useState([]);

  // Helper function to map status to color scheme
  const getColor = (status) => {
    switch(status) {
      case 'Pending':
        return 'accent';
      case 'In Progress':
        return 'brand';
      case 'Completed':
        return 'green';
      default:
        return 'slate';
    }
  };

  const statusFilters = ['', 'Pending', 'In Progress', 'Completed'];
  const statusCounts = {
    all: complaintsList.length,
    Pending: complaintsList.filter((c) => c.status === 'Pending').length,
    'In Progress': complaintsList.filter((c) => c.status === 'In Progress').length,
    Completed: complaintsList.filter((c) => c.status === 'Completed').length,
  };

  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isOpenDelete, onOpen: onOpenDelete, onClose: onCloseDelete } = useDisclosure();
  const { isOpen: isOpenZoom, onOpen: onOpenZoom, onClose: onCloseZoom } = useDisclosure();
  const toast = useToast();
  const [editingComplaint, setEditingComplaint] = useState(null);
  const [deletingComplaint, setDeletingComplaint] = useState(null);
  const [zoomedImage, setZoomedImage] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [allImages, setAllImages] = useState([]);

  // Technicians list state
  const [techniciansList, setTechniciansList] = useState([]);
  const [technicianSearchTerm, setTechnicianSearchTerm] = useState('');
  const [showTechnicianDropdown, setShowTechnicianDropdown] = useState(false);

  // State variables for the new fields
  const [newCustomerName, setNewCustomerName] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [newLatitude, setNewLatitude] = useState(null);
  const [newLongitude, setNewLongitude] = useState(null);
  const [newAddress, setNewAddress] = useState('');
  const [newServiceType, setNewServiceType] = useState('');
  const [servicesList, setServicesList] = useState([]);
  const [newAssignedTechnicians, setNewAssignedTechnicians] = useState([]);
  const [newStatus, setNewStatus] = useState('Pending');
  const [newDescription, setNewDescription] = useState('');
  const [newPhotosBefore, setNewPhotosBefore] = useState([]);
  const [newPhotosAfter, setNewPhotosAfter] = useState([]);
  const [newCustomerRating, setNewCustomerRating] = useState('');
  const [newCustomerComments, setNewCustomerComments] = useState('');
  const [customerLookupLoading, setCustomerLookupLoading] = useState(false);
  const lastLookupPhoneRef = React.useRef('');
  
  // Filter state
  const [filterStatus, setFilterStatus] = useState('');
  const filteredComplaints = complaintsList.filter(c =>
    filterStatus ? c.status === filterStatus : true
  );

  // Inventory modal state
  const [isOpenInventory, setIsOpenInventory] = useState(false);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [inventoryLoading, setInventoryLoading] = useState(false);
  const [inventoryError, setInventoryError] = useState('');
  const [inventoryComplaintId, setInventoryComplaintId] = useState(null);

  // Fetch data on component mount
  React.useEffect(() => {
    fetchComplaints();
    fetchTechnicians();
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const response = await getServices();
      if (response.data?.data) {
        setServicesList(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching services:', error);
    }
  };

  const activeServices = servicesList.filter((service) => service.status === 'Active');

  const serviceTypeOptions = React.useMemo(() => {
    const options = [...activeServices];
    if (newServiceType && !options.some((service) => service.name === newServiceType)) {
      options.unshift({ id: `legacy-${newServiceType}`, name: newServiceType, status: 'Legacy' });
    }
    return options;
  }, [activeServices, newServiceType]);

  React.useEffect(() => {
    if (!newServiceType && activeServices.length > 0) {
      setNewServiceType(activeServices[0].name);
    }
  }, [servicesList, newServiceType, activeServices]);

  const getDefaultServiceType = () => {
    if (activeServices.length > 0) {
      return activeServices[0].name;
    }
    return '';
  };

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (showTechnicianDropdown && !event.target.closest('[data-technician-dropdown]')) {
        setShowTechnicianDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showTechnicianDropdown]);

  const normalizePhotoList = (photos) => {
    if (!photos) return [];
    if (Array.isArray(photos)) return photos;
    if (typeof photos === 'string') {
      try {
        const parsed = JSON.parse(photos);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  };

  // Helper function to convert backend field names to frontend field names
  const mapComplaintFields = (complaint) => {
    return {
      ...complaint,
      customerName: complaint.customer_name,
      complaintType: complaint.complaint_type,
      serviceType: complaint.service_type || complaint.complaint_type,
      serviceDescription: complaint.service_description,
      assignedTechnician: complaint.assigned_technician,
      assignedTechnicians: complaint.assigned_technicians
        || (complaint.assigned_technician ? [complaint.assigned_technician] : []),
      technicianName: complaint.technician_names || complaint.technician_name,
      technicianNames: complaint.technician_names || complaint.technician_name,
      customerRating: complaint.customer_rating,
      customerComments: complaint.customer_comments,
      createdTime: complaint.created_time,
      completedTime: complaint.completed_time,
      createdAt: complaint.created_at,
      modifiedAt: complaint.modified_at,
      photosBefore: normalizePhotoList(complaint.photos_before),
      photosAfter: normalizePhotoList(complaint.photos_after),
      latitude: complaint.latitude,
      longitude: complaint.longitude,
    };
  };

  // Helper function to convert relative image paths to full URLs
  const getFullImageUrl = getUploadUrl;

  // Fetch complaints from backend
  const fetchComplaints = async () => {
    try {
      const response = await getComplaints();
      if (response.data && response.data.data) {
        // Map field names from snake_case to camelCase
        const mappedComplaints = response.data.data.map(mapComplaintFields);
        setComplaintsList(mappedComplaints);
      }
    } catch (error) {
      console.error('Error fetching complaints:', error);
    }
  };

  // Fetch technicians from backend
  const fetchTechnicians = async () => {
    try {
      const response = await getTechnicians();
      if (response.data && response.data.data) {
        setTechniciansList(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching technicians:', error);
    }
  };

  // Filter technicians based on search term
  const filteredTechnicians = techniciansList.filter(tech =>
    tech.name.toLowerCase().includes(technicianSearchTerm.toLowerCase())
  );

  // Helper function to convert files to base64 for preview and storage
  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  // Handle multiple file uploads for before photos
  const handlePhotosBeforeUpload = async (e) => {
    const files = Array.from(e.target.files);
    const fileObjects = files.map(file => ({
      file: file,
      name: file.name,
      data: URL.createObjectURL(file), // For preview
      size: file.size
    }));
    setNewPhotosBefore([...newPhotosBefore, ...fileObjects]);
  };

  // Handle multiple file uploads for after photos
  const handlePhotosAfterUpload = async (e) => {
    const files = Array.from(e.target.files);
    const fileObjects = files.map(file => ({
      file: file,
      name: file.name,
      data: URL.createObjectURL(file), // For preview
      size: file.size
    }));
    setNewPhotosAfter([...newPhotosAfter, ...fileObjects]);
  };

  // Remove image from before photos
  const removePhotoBeforeByIndex = (index) => {
    setNewPhotosBefore(newPhotosBefore.filter((_, i) => i !== index));
  };

  // Remove image from after photos
  const removePhotoAfterByIndex = (index) => {
    setNewPhotosAfter(newPhotosAfter.filter((_, i) => i !== index));
  };

  // Handle image zoom
  const handleImageZoom = (imageData, imageName, imageIndex = 0, imageGallery = []) => {
    setZoomedImage({ data: imageData, name: imageName });
    setCurrentImageIndex(imageIndex);
    setAllImages(imageGallery);
    onOpenZoom();
  };

  // Navigate to previous image
  const handlePreviousImage = () => {
    if (allImages.length > 1) {
      const newIndex = currentImageIndex > 0 ? currentImageIndex - 1 : allImages.length - 1;
      setCurrentImageIndex(newIndex);
      setZoomedImage(allImages[newIndex]);
    }
  };

  // Navigate to next image
  const handleNextImage = () => {
    if (allImages.length > 1) {
      const newIndex = currentImageIndex < allImages.length - 1 ? currentImageIndex + 1 : 0;
      setCurrentImageIndex(newIndex);
      setZoomedImage(allImages[newIndex]);
    }
  };

  const handleAdd = () => {
    setEditingComplaint(null);
    setNewCustomerName('');
    setNewPhone('');
    setNewLatitude(null);
    setNewLongitude(null);
    setNewAddress('');
    setNewServiceType(getDefaultServiceType());
    setNewAssignedTechnicians([]);
    setNewStatus('Pending');
    setNewDescription('');
    setNewPhotosBefore([]);
    setNewPhotosAfter([]);
    setNewCustomerRating('');
    setNewCustomerComments('');
    setTechnicianSearchTerm('');
    setShowTechnicianDropdown(false);
    setCustomerLookupLoading(false);
    lastLookupPhoneRef.current = '';
    onOpen();
  };

  const lookupCustomerDetails = async (phone) => {
    const digits = normalizePhoneDigits(phone);
    if (digits.length < 10 || digits === lastLookupPhoneRef.current) {
      return;
    }

    lastLookupPhoneRef.current = digits;
    setCustomerLookupLoading(true);
    try {
      const response = await lookupCustomerByPhone(digits);
      const customer = response.data?.data;
      if (customer) {
        setNewCustomerName(customer.customer_name || '');
        setNewAddress(customer.address || '');
        if (customer.latitude != null && customer.longitude != null) {
          setNewLatitude(Number(customer.latitude));
          setNewLongitude(Number(customer.longitude));
        }
        toast({
          title: 'Customer details loaded',
          description: 'Name and address filled from previous complaint.',
          status: 'info',
          duration: 2500,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Customer lookup failed:', error);
    } finally {
      setCustomerLookupLoading(false);
    }
  };

  const handlePhoneChange = (e) => {
    const value = e.target.value;
    setNewPhone(value);
    if (normalizePhoneDigits(value).length === 10) {
      lookupCustomerDetails(value);
    }
  };

  const handleLocationChange = ({ lat, lng, address }) => {
    setNewLatitude(lat);
    setNewLongitude(lng);
    if (address) {
      setNewAddress(address);
    }
  };

  const handleAddressPlaceSelect = ({ address, lat, lng }) => {
    setNewAddress(address);
    setNewLatitude(lat);
    setNewLongitude(lng);
  };

  const handleEdit = (complaint) => {
    setEditingComplaint(complaint);
    setNewCustomerName(complaint.customerName);
    setNewPhone(complaint.phone);
    setNewLatitude(complaint.latitude != null ? Number(complaint.latitude) : null);
    setNewLongitude(complaint.longitude != null ? Number(complaint.longitude) : null);
    setNewAddress(complaint.address);
    setNewServiceType(complaint.serviceType || complaint.complaintType || getDefaultServiceType());
    const techIds = (complaint.assignedTechnicians && complaint.assignedTechnicians.length > 0)
      ? complaint.assignedTechnicians
      : (complaint.assignedTechnician ? [complaint.assignedTechnician] : []);
    setNewAssignedTechnicians(techIds.map(id => id.toString()));
    setNewStatus(complaint.status);
    setNewDescription(complaint.description);
    // Convert URL strings back to display objects for editing (if they exist)
    setNewPhotosBefore(Array.isArray(complaint.photosBefore) ? complaint.photosBefore.map(url => ({ 
      data: getFullImageUrl(url), 
      name: 'existing-image', 
      file: null,
      originalPath: url // Store original path for backend
    })) : []);
    setNewPhotosAfter(Array.isArray(complaint.photosAfter) ? complaint.photosAfter.map(url => ({ 
      data: getFullImageUrl(url), 
      name: 'existing-image', 
      file: null,
      originalPath: url // Store original path for backend
    })) : []);
    setNewCustomerRating(complaint.customerRating || '');
    setNewCustomerComments(complaint.customerComments);
    setTechnicianSearchTerm('');
    setShowTechnicianDropdown(false);
    onOpen();
  };

  const handleSave = async () => {
    if (!newServiceType) {
      toast({ title: 'Please select a service type', status: 'warning', duration: 3000 });
      return;
    }
    if (newLatitude == null || newLongitude == null) {
      if (!editingComplaint) {
        toast({
          title: 'Select service location',
          description: 'Tap the map or use current location to place the marker.',
          status: 'warning',
          duration: 3000,
          isClosable: true,
        });
        return;
      }
    }
    const currentTime = new Date().toLocaleString();
    
    // Use FormData when uploading new files or preserving existing saved photos
    const shouldUseFormData =
      newPhotosBefore.some((photo) => photo.file) ||
      newPhotosAfter.some((photo) => photo.file) ||
      newPhotosBefore.some((photo) => photo.originalPath) ||
      newPhotosAfter.some((photo) => photo.originalPath);
    
    if (shouldUseFormData) {
      // Use FormData for file uploads
      const formData = new FormData();
      
      // Add text fields
      formData.append('customerName', newCustomerName);
      formData.append('phone', newPhone);
      if (newLatitude != null) formData.append('latitude', String(newLatitude));
      if (newLongitude != null) formData.append('longitude', String(newLongitude));
      formData.append('address', newAddress);
      formData.append('complaintType', newServiceType);
      newAssignedTechnicians.forEach((techId) => {
        formData.append('assignedTechnicians', techId);
      });
      formData.append('status', newStatus);
      formData.append('description', newDescription);
      formData.append('customerRating', newCustomerRating || '');
      formData.append('customerComments', newCustomerComments);
      formData.append('createdTime', editingComplaint ? editingComplaint.createdTime : currentTime);
      formData.append('completedTime', newStatus === 'Completed' && (!editingComplaint || !editingComplaint.completedTime) ? currentTime : (editingComplaint ? editingComplaint.completedTime || '' : ''));
      
      if (editingComplaint) {
        formData.append('id', editingComplaint.id);
      }
      
      // Add files
      newPhotosBefore.forEach((photo) => {
        if (photo.file) {
          formData.append('photosBefore', photo.file);
        } else if (photo.originalPath) {
          // Use original path for existing photos
          formData.append('existingPhotosBefore', photo.originalPath);
        }
      });
      
      newPhotosAfter.forEach((photo) => {
        if (photo.file) {
          formData.append('photosAfter', photo.file);
        } else if (photo.originalPath) {
          // Use original path for existing photos
          formData.append('existingPhotosAfter', photo.originalPath);
        }
      });

      try {
        if (editingComplaint) {
          await updateComplaint(formData);
        } else {
          await createComplaint(formData);
        }
        
        // Refresh the complaints list
        fetchComplaints();
        setEditingComplaint(null);
        onClose();
      } catch (error) {
        console.error('Error saving complaint:', error);
        // You can add a toast notification here for error handling
      }
    } else {
      // No files, use regular JSON
      const complaintData = {
        customerName: newCustomerName,
        phone: newPhone,
        latitude: newLatitude,
        longitude: newLongitude,
        address: newAddress,
        complaintType: newServiceType,
        assignedTechnicians: newAssignedTechnicians.map(id => parseInt(id, 10)),
        status: newStatus,
        description: newDescription,
        photosBefore: newPhotosBefore.filter(photo => photo.originalPath).map(photo => photo.originalPath),
        photosAfter: newPhotosAfter.filter(photo => photo.originalPath).map(photo => photo.originalPath),
        customerRating: newCustomerRating ? parseInt(newCustomerRating) : null,
        customerComments: newCustomerComments,
        createdTime: editingComplaint ? editingComplaint.createdTime : currentTime,
        completedTime: newStatus === 'Completed' && (!editingComplaint || !editingComplaint.completedTime) ? currentTime : (editingComplaint ? editingComplaint.completedTime : null)
      };

      try {
        if (editingComplaint) {
          await updateComplaint({ ...complaintData, id: editingComplaint.id });
        } else {
          await createComplaint(complaintData);
        }
        
        // Refresh the complaints list
        fetchComplaints();
        setEditingComplaint(null);
        onClose();
      } catch (error) {
        console.error('Error saving complaint:', error);
        // You can add a toast notification here for error handling
      }
    }
  };

  const handleDeleteClick = (complaint) => {
    setDeletingComplaint(complaint);
    onOpenDelete();
  };

  const handleShareRating = async (complaintId) => {
    try {
      // Find the complaint to get the phone number
      const complaint = complaintsList.find(c => c.id === complaintId);

      if (!complaint) {
        toast({
          title: "Complaint not found",
          description: "Unable to find complaint details.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      const customerPhone = complaint.phone;
      const customerName = complaint.customerName || 'Customer';

      if (!customerPhone) {
        toast({
          title: "No phone number",
          description: "This complaint doesn't have a phone number.",
          status: "error",
          duration: 3000,
          isClosable: true,
        });
        return;
      }

      // Clean the phone number for wa.me (international format, no + prefix).
      const cleanPhone = formatWhatsAppPhone(customerPhone);

      // Create the rating URL and fetch service report PDF
      const ratingUrl = `${BASE_URL}/api/complaints/rate/${complaintId}`;
      const pdfUrl = `${BASE_URL}/api/complaints/${complaintId}/service-report`;

      const whatsappMessage = [
        `Hi ${customerName}! 👋`,
        '',
        '*Terracool Service Completed*',
        '',
        'Please find your service report attached.',
        'Complaint, service, and technician details are in the PDF.',
        '',
        '*Service Report (PDF):*',
        pdfUrl,
        '',
        '*Rate your experience:* 🌟',
        ratingUrl,
        '',
        'Thank you for choosing Terracool!',
      ].join('\n');

      const whatsappUrl = `https://wa.me/${cleanPhone}?text=${encodeURIComponent(whatsappMessage)}`;

      const openWhatsAppChat = () => {
        window.location.href = whatsappUrl;
      };

      try {
        const pdfResponse = await getServiceReportPdf(complaintId);
        const pdfBlob = pdfResponse.data;
        const fileName = `Terracool-Service-Report-${complaintId}.pdf`;

        const blobUrl = URL.createObjectURL(pdfBlob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);

        // Always open wa.me so WhatsApp targets this customer's chat (mobile + web).
        // Web Share API cannot pre-select a recipient, which is why the app asked "who to send to".
        setTimeout(openWhatsAppChat, 400);

        toast({
          title: "Opening WhatsApp",
          description: `PDF downloaded. Attach it in the chat with ${customerName}.`,
          status: "info",
          duration: 5000,
          isClosable: true,
        });
      } catch (shareError) {
        openWhatsAppChat();
        toast({
          title: "Opening WhatsApp",
          description: `Sending rating request and PDF link to ${customerName}`,
          status: "info",
          duration: 3000,
          isClosable: true,
        });
      }

    } catch (error) {
      console.error('Error sharing rating link:', error);
      toast({
        title: "Share failed",
        description: "Unable to share the rating link. Please try again.",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const confirmDelete = async () => {
    try {
      await deleteComplaint(deletingComplaint.id);
      // Refresh the complaints list
      fetchComplaints();
      setDeletingComplaint(null);
      onCloseDelete();
    } catch (error) {
      console.error('Error deleting complaint:', error);
      // You can add a toast notification here for error handling
    }
  };

  // Fetch inventory for a complaint
  const handleViewInventory = async (complaintId) => {
    setInventoryComplaintId(complaintId);
    setInventoryLoading(true);
    setInventoryError('');
    setInventoryItems([]);
    setIsOpenInventory(true);
    try {
      const res = await getComplaintInventory(complaintId);
      if (res.data && res.data.status === 'success') {
        setInventoryItems(res.data.data);
      } else {
        setInventoryError(res.data?.message || 'Failed to fetch inventory');
      }
    } catch (e) {
      setInventoryError('Failed to fetch inventory');
    }
    setInventoryLoading(false);
  };

  return (
    <Box>
      <Tabs variant="soft-pill" colorScheme="brand" mb={6}>
        <TabList>
          <Tab>Complaints</Tab>
          <Tab>Services</Tab>
          <Tab>Map</Tab>
        </TabList>
        <TabPanels>
          <TabPanel px={0} pt={5}>
            <PageShell
              title="Complaints"
              subtitle="Track service requests, assignments, and customer feedback"
              action={
                <Button leftIcon={<AddIcon />} colorScheme="brand" size="md" borderRadius="lg" onClick={handleAdd}>
                  New Complaint
                </Button>
              }
            />

            <SimpleGrid columns={{ base: 2, lg: 4 }} spacing={4} mb={6}>
              <StatTile label="Total" value={statusCounts.all} accent="slate.500" icon={<Icon as={FaClipboardList} />} />
              <StatTile label="Pending" value={statusCounts.Pending} accent="accent.500" icon={<Icon as={FaClock} />} />
              <StatTile label="In Progress" value={statusCounts['In Progress']} accent="brand.500" icon={<Icon as={FaSpinner} />} />
              <StatTile label="Completed" value={statusCounts.Completed} accent="green.500" icon={<Icon as={FaCheckCircle} />} />
            </SimpleGrid>

            <HStack spacing={2} mb={5} flexWrap="wrap">
              {statusFilters.map((status) => {
                const label = status || 'All';
                const count = status ? statusCounts[status] : statusCounts.all;
                const isActive = filterStatus === status;
                return (
                  <Button
                    key={label}
                    size="sm"
                    borderRadius="full"
                    variant={isActive ? 'solid' : 'outline'}
                    colorScheme={isActive ? 'brand' : 'slate'}
                    bg={isActive ? 'brand.500' : 'white'}
                    borderColor="slate.200"
                    onClick={() => setFilterStatus(status)}
                  >
                    {label} ({count})
                  </Button>
                );
              })}
            </HStack>

            <DataCard>
              <TableContainer overflowX="auto">
                <Table variant="modern" size="sm">
        <Thead>
          <Tr>
            <Th>ID</Th>
            <Th>Customer</Th>
            <Th>Phone</Th>
            <Th>Address</Th>
            <Th>Service</Th>
            <Th>Technicians</Th>
            <Th>Status</Th>
            <Th>Description</Th>
            <Th>Rating</Th>
            <Th>Created</Th>
            <Th>Completed</Th>
            <Th textAlign="right">Actions</Th>
          </Tr>
        </Thead>
        <Tbody>
          {filteredComplaints.map(complaint => (
            <Tr key={complaint.id}>
              <Td fontWeight="600" color="slate.800">#{complaint.id}</Td>
              <Td fontWeight="500">{complaint.customerName}</Td>
              <Td>{complaint.phone}</Td>
              <Td>{complaint.address}</Td>
              <Td>{complaint.serviceType || complaint.complaintType}</Td>
              <Td>{complaint.technicianNames || complaint.technicianName || '-'}</Td>
              <Td>
                <Badge colorScheme={getColor(complaint.status)} variant="subtle">
                  {complaint.status}
                </Badge>
              </Td>
              <Td maxW="200px" isTruncated title={complaint.description}>{complaint.description}</Td>
              <Td>
                {complaint.customerRating ? `${complaint.customerRating}/5` : '-'}
              </Td>
              <Td>{complaint.createdTime}</Td>
              <Td>{complaint.completedTime || '-'}</Td>
              <Td textAlign="right">
                <Flex direction="row" justify="flex-end" gap={1}>
                  <IconButton aria-label="View Inventory" icon={<ViewIcon />} size="sm" variant="ghost" colorScheme="brand" onClick={() => handleViewInventory(complaint.id)} />
                  <IconButton aria-label="Edit Complaint" icon={<EditIcon />} size="sm" variant="ghost" colorScheme="brand" onClick={() => handleEdit(complaint)} />
                  {complaint.status === 'Completed' && !complaint.customerRating && (
                    <IconButton
                      aria-label="Share Rating Link"
                      icon={<Text fontSize="14px">📤</Text>}
                      size="sm"
                      variant="ghost"
                      colorScheme="green"
                      onClick={() => handleShareRating(complaint.id)}
                      title="Share rating link with customer"
                    />
                  )}
                  <IconButton aria-label="Delete Complaint" icon={<DeleteIcon />} size="sm" variant="ghost" colorScheme="red" onClick={() => handleDeleteClick(complaint)} />
                </Flex>
              </Td>
            </Tr>
          ))}
        </Tbody>
      </Table>
              </TableContainer>
              {filteredComplaints.length === 0 && (
                <Box py={12} textAlign="center">
                  <Text color="slate.500" fontSize="sm">No complaints match this filter.</Text>
                </Box>
              )}
            </DataCard>

      {/* Add/Edit Complaint Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
        <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(4px)" />
        <ModalContent
          size="6xl"
          borderRadius="2xl"
          mx={4}
          my={4}
          maxH="90vh"
          display="flex"
          flexDirection="column"
        >
          <ModalHeader
            textAlign="center"
            fontFamily="heading"
            fontWeight="700"
            borderBottom="1px solid"
            borderColor="slate.100"
            py={5}
            flexShrink={0}
          >
            {editingComplaint ? 'Edit Complaint' : 'New Complaint'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody px={6} py={6} overflowY="auto" flex="1" minH={0}>
            <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={6}>
              <FormControl>
                <FormLabel>Customer Name</FormLabel>
                <Input 
                  value={newCustomerName} 
                  onChange={(e) => setNewCustomerName(e.target.value)} 
                  placeholder="Enter customer name" 
                />
              </FormControl>
              <FormControl>
                <FormLabel>Phone</FormLabel>
                <Input 
                  value={newPhone} 
                  onChange={handlePhoneChange}
                  onBlur={() => lookupCustomerDetails(newPhone)}
                  placeholder="Enter phone number"
                  isDisabled={customerLookupLoading}
                />
              </FormControl>
            </Grid>
            <FormControl mt={6} isRequired>
              <FormLabel>Address</FormLabel>
              <AddressAutocomplete
                value={newAddress}
                onChange={setNewAddress}
                onPlaceSelect={handleAddressPlaceSelect}
                placeholder="Start typing an address..."
              />
            </FormControl>
            <FormControl mt={6} isRequired>
              <FormLabel>Service Location</FormLabel>
              <LocationPicker
                value={
                  newLatitude != null && newLongitude != null
                    ? { lat: newLatitude, lng: newLongitude }
                    : null
                }
                onChange={handleLocationChange}
              />
            </FormControl>
            <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={6} mt={6}>
              <FormControl isRequired>
                <FormLabel>Service Type</FormLabel>
                <Select 
                  value={newServiceType} 
                  onChange={(e) => setNewServiceType(e.target.value)}
                  placeholder={activeServices.length === 0 ? 'Add services in Services tab first' : 'Select service type'}
                  isDisabled={activeServices.length === 0}
                >
                  {serviceTypeOptions.map((service) => (
                    <option key={service.id} value={service.name}>
                      {service.name}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>Assigned Technicians (Group)</FormLabel>
                <Box position="relative" data-technician-dropdown>
                  <HStack spacing={0}>
                    <Input 
                      value={technicianSearchTerm} 
                      onChange={(e) => {
                        setTechnicianSearchTerm(e.target.value);
                        setShowTechnicianDropdown(true);
                      }}
                      onFocus={() => setShowTechnicianDropdown(true)}
                      placeholder="Search and add technicians to the group..." 
                      autoComplete="off"
                      borderRightRadius="0"
                    />
                    <Button
                      onClick={() => setShowTechnicianDropdown(!showTechnicianDropdown)}
                      borderLeftRadius="0"
                      borderLeft="none"
                      px={3}
                      bg="gray.100"
                      _hover={{ bg: "gray.200" }}
                    >
                      <ChevronDownIcon />
                    </Button>
                  </HStack>
                  {newAssignedTechnicians.length > 0 && (
                    <HStack mt={2} flexWrap="wrap" spacing={2}>
                      {newAssignedTechnicians.map((techId) => {
                        const tech = techniciansList.find(t => t.id.toString() === techId.toString());
                        return (
                          <Badge
                            key={techId}
                            colorScheme="blue"
                            px={2}
                            py={1}
                            borderRadius="md"
                            display="flex"
                            alignItems="center"
                            gap={1}
                          >
                            {tech?.name || `ID ${techId}`}
                            <CloseIcon
                              boxSize={2}
                              cursor="pointer"
                              onClick={() => setNewAssignedTechnicians(
                                newAssignedTechnicians.filter(id => id !== techId)
                              )}
                            />
                          </Badge>
                        );
                      })}
                    </HStack>
                  )}
                  {showTechnicianDropdown && (
                    <Box
                      position="absolute"
                      top="100%"
                      left="0"
                      right="0"
                      bg="white"
                      border="1px solid"
                      borderColor="gray.200"
                      borderRadius="md"
                      boxShadow="lg"
                      zIndex="1000"
                      maxH="200px"
                      overflowY="auto"
                      mt="1"
                    >
                      {filteredTechnicians.length > 0 ? (
                        filteredTechnicians.map((tech, index) => {
                          const techIdStr = tech.id.toString();
                          const isSelected = newAssignedTechnicians.includes(techIdStr);
                          return (
                            <Box
                              key={tech.id}
                              px={3}
                              py={2}
                              cursor="pointer"
                              bg={isSelected ? "blue.50" : "white"}
                              _hover={{ bg: isSelected ? "blue.100" : "gray.100" }}
                              borderBottom={index < filteredTechnicians.length - 1 ? "1px solid" : "none"}
                              borderBottomColor="gray.100"
                              onClick={() => {
                                if (isSelected) {
                                  setNewAssignedTechnicians(
                                    newAssignedTechnicians.filter(id => id !== techIdStr)
                                  );
                                } else {
                                  setNewAssignedTechnicians([...newAssignedTechnicians, techIdStr]);
                                }
                                setTechnicianSearchTerm('');
                              }}
                            >
                              <Text fontWeight="medium">
                                {isSelected ? '✓ ' : ''}{tech.name}
                              </Text>
                              <Text fontSize="sm" color="gray.600">
                                {tech.area} • {tech.status}
                              </Text>
                            </Box>
                          );
                        })
                      ) : (
                        <Box px={3} py={2} color="gray.500">
                          No technicians found
                        </Box>
                      )}
                    </Box>
                  )}
                </Box>
              </FormControl>
            </Grid>
            <FormControl mt={6}>
              <FormLabel>Status</FormLabel>
              <Select 
                value={newStatus} 
                onChange={(e) => setNewStatus(e.target.value)}
              >
                <option value="Pending">Pending</option>
                <option value="In Progress">In Progress</option>
                <option value="Completed">Completed</option>
              </Select>
            </FormControl>
            <FormControl mt={6}>
              <FormLabel>Description</FormLabel>
              <Textarea 
                value={newDescription} 
                onChange={(e) => setNewDescription(e.target.value)} 
                placeholder="Describe the complaint details" 
              />
            </FormControl>
            <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={6} mt={6}>
              <FormControl>
                <FormLabel>Photos Before Work</FormLabel>
                <Input 
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handlePhotosBeforeUpload}
                  mb={3}
                />
                {newPhotosBefore.length > 0 && (
                  <SimpleGrid columns={3} spacing={3}>
                    {newPhotosBefore.map((photo, index) => (
                      <Box key={index} position="relative" width="120px" height="120px">
                        <Box
                          position="relative"
                          width="100%"
                          height="100%"
                          borderRadius="md"
                          overflow="hidden"
                          border="2px solid"
                          borderColor="gray.200"
                          cursor="pointer"
                          _hover={{ borderColor: "blue.300" }}
                          onClick={() => {
                            const gallery = newPhotosBefore.map(p => ({ data: p.data, name: p.name }));
                            handleImageZoom(photo.data, photo.name, index, gallery);
                          }}
                        >
                          <Image 
                            src={photo.data} 
                            alt={`Before ${index + 1}`}
                            width="100%"
                            height="100%"
                            objectFit="cover"
                          />
                        </Box>
                        <IconButton
                          aria-label="Remove image"
                          icon={<CloseIcon />}
                          size="xs"
                          colorScheme="red"
                          position="absolute"
                          top="2px"
                          right="2px"
                          borderRadius="full"
                          zIndex="2"
                          onClick={(e) => {
                            e.stopPropagation();
                            removePhotoBeforeByIndex(index);
                          }}
                        />
                      </Box>
                    ))}
                  </SimpleGrid>
                )}
              </FormControl>
              <FormControl>
                <FormLabel>Photos After Work</FormLabel>
                <Input 
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={handlePhotosAfterUpload}
                  mb={3}
                />
                {newPhotosAfter.length > 0 && (
                  <SimpleGrid columns={3} spacing={3}>
                    {newPhotosAfter.map((photo, index) => (
                      <Box key={index} position="relative" width="120px" height="120px">
                        <Box
                          position="relative"
                          width="100%"
                          height="100%"
                          borderRadius="md"
                          overflow="hidden"
                          border="2px solid"
                          borderColor="gray.200"
                          cursor="pointer"
                          _hover={{ borderColor: "blue.300" }}
                          onClick={() => {
                            const gallery = newPhotosAfter.map(p => ({ data: p.data, name: p.name }));
                            handleImageZoom(photo.data, photo.name, index, gallery);
                          }}
                        >
                          <Image 
                            src={photo.data} 
                            alt={`After ${index + 1}`}
                            width="100%"
                            height="100%"
                            objectFit="cover"
                          />
                        </Box>
                        <IconButton
                          aria-label="Remove image"
                          icon={<CloseIcon />}
                          size="xs"
                          colorScheme="red"
                          position="absolute"
                          top="2px"
                          right="2px"
                          borderRadius="full"
                          zIndex="2"
                          onClick={(e) => {
                            e.stopPropagation();
                            removePhotoAfterByIndex(index);
                          }}
                        />
                      </Box>
                    ))}
                  </SimpleGrid>
                )}
              </FormControl>
            </Grid>
            <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={6} mt={6}>
              <FormControl>
                <FormLabel>Customer Rating (1-5)</FormLabel>
                <Select 
                  value={newCustomerRating} 
                  onChange={(e) => setNewCustomerRating(e.target.value)}
                >
                  <option value="">No Rating</option>
                  <option value="1">1 Star</option>
                  <option value="2">2 Stars</option>
                  <option value="3">3 Stars</option>
                  <option value="4">4 Stars</option>
                  <option value="5">5 Stars</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>Customer Comments</FormLabel>
                <Textarea 
                  value={newCustomerComments} 
                  onChange={(e) => setNewCustomerComments(e.target.value)} 
                  placeholder="Customer feedback comments" 
                />
              </FormControl>
            </Grid>
          </ModalBody>
          <ModalFooter justifyContent="flex-end" gap={3} borderTop="1px solid" borderColor="slate.100" py={4} px={6} flexShrink={0}>
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button colorScheme="brand" onClick={handleSave}>Save Complaint</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isOpenDelete} onClose={() => { onCloseDelete(); setDeletingComplaint(null); }}>
        <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(4px)" />
        <ModalContent borderRadius="2xl" mx={4}>
          <ModalHeader fontFamily="heading">Delete Complaint</ModalHeader>
          <ModalCloseButton />
          <ModalBody textAlign="center">
            {deletingComplaint && (
              <Box>
                Are you sure you want to delete complaint from <strong>{deletingComplaint.customerName}</strong>?
              </Box>
            )}
          </ModalBody>
          <ModalFooter justifyContent="center">
            <Button colorScheme="red" mx={3} onClick={confirmDelete}>
              Delete
            </Button>
            <Button variant="ghost" mx={3} onClick={() => { onCloseDelete(); setDeletingComplaint(null); }}>
              Cancel
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Image Zoom Modal */}
      <Modal isOpen={isOpenZoom} onClose={onCloseZoom} size="6xl">
        <ModalOverlay bg="blackAlpha.800" />
        <ModalContent bg="transparent" boxShadow="none" maxW="90vw" maxH="90vh">
          <ModalCloseButton 
            color="white" 
            bg="blackAlpha.600" 
            _hover={{ bg: "blackAlpha.800" }}
            width="60px"
            height="60px"
            fontSize="24px"
            top="4"
            right="4"
            zIndex="20"
          />
          <ModalBody p={0} display="flex" alignItems="center" justifyContent="center" position="relative">
            {zoomedImage && (
              <>
                {/* Previous Button */}
                {allImages.length > 1 && (
                  <IconButton
                    aria-label="Previous image"
                    icon={<ChevronLeftIcon />}
                    position="absolute"
                    left="4"
                    top="50%"
                    transform="translateY(-50%)"
                    zIndex="10"
                    width="80px"
                    height="80px"
                    fontSize="32px"
                    bg="blackAlpha.600"
                    color="white"
                    _hover={{ bg: "blackAlpha.800" }}
                    onClick={handlePreviousImage}
                  />
                )}

                {/* Image Display */}
                <VStack spacing={4}>
                  <Image 
                    src={zoomedImage.data} 
                    alt={zoomedImage.name}
                    maxW="100%"
                    maxH="80vh"
                    objectFit="contain"
                    borderRadius="md"
                  />
                  {allImages.length > 1 && (
                    <Text 
                      color="white" 
                      fontSize="md" 
                      textAlign="center"
                      bg="blackAlpha.600"
                      px={3}
                      py={2}
                      borderRadius="md"
                    >
                      {currentImageIndex + 1} / {allImages.length}
                    </Text>
                  )}
                </VStack>

                {/* Next Button */}
                {allImages.length > 1 && (
                  <IconButton
                    aria-label="Next image"
                    icon={<ChevronRightIcon />}
                    position="absolute"
                    right="4"
                    top="50%"
                    transform="translateY(-50%)"
                    zIndex="10"
                    width="80px"
                    height="80px"
                    fontSize="32px"
                    bg="blackAlpha.600"
                    color="white"
                    _hover={{ bg: "blackAlpha.800" }}
                    onClick={handleNextImage}
                  />
                )}
              </>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Inventory Modal */}
      <Modal isOpen={isOpenInventory} onClose={() => setIsOpenInventory(false)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader textAlign="center">Complaint Inventory</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {inventoryLoading ? (
              <Box textAlign="center" py={6}>Loading...</Box>
            ) : inventoryError ? (
              <Box color="red.500" textAlign="center" py={6}>{inventoryError}</Box>
            ) : inventoryItems.length === 0 ? (
              <Box textAlign="center" py={6}>No inventory items for this complaint.</Box>
            ) : (
              <Table size="sm" variant="simple">
                <Thead>
                  <Tr>
                    <Th>Material Name</Th>
                    <Th>Quantity</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {inventoryItems.map((item, idx) => (
                    <Tr key={idx}>
                      <Td>{item.material_name}</Td>
                      <Td>{item.quantity}</Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            )}
          </ModalBody>
          <ModalFooter justifyContent="center">
            <Button variant="ghost" onClick={() => setIsOpenInventory(false)}>Close</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
          </TabPanel>
          <TabPanel px={0} pt={5}>
            <Services servicesList={servicesList} onServicesChange={fetchServices} />
          </TabPanel>
          <TabPanel px={0} pt={5}>
            <ComplaintsMap complaints={complaintsList} />
          </TabPanel>
        </TabPanels>
      </Tabs>
    </Box>
  );
};

export default Complaints;
